import { Image } from 'expo-image';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { IncentiveEvaluationErrorCodes } from '@fhs/loyalty/src/types';
import {
  Box,
  FormControl,
  HStack,
  Icon,
  InlineAlert,
  InlineAlertProps,
} from '@fhs-legacy/universal-components';
import { TextInput } from 'components/ucl/text-input';
import { PromotionType } from 'generated/graphql-gateway';
import { selectors, useAppSelector } from 'state/global-state';
import { useLocale } from 'state/intl';
import { LANGUAGES } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { DateAndTimeErrors } from 'state/loyalty/hooks/types';
import { getEvaluationResultMessage } from 'state/loyalty/hooks/utils/helpers';
import { useOrderContext } from 'state/order';
import { getPhoneNumberErrorMessage, isPhoneNumberValid } from 'utils/form';
import { IPlaceAddress } from 'utils/geolocation';
import { formatDistanceToNow } from 'utils/language/format-date';

import { getLocalizedDeliveryAddress } from './common';
import { ITU_COUNTRY_CODES, PHONE_NUMBER_LENGTH } from './constants';
import PhoneAddressInformationToggle from './phone-address-information-toggle';
import SaveAddressSelection from './save-address-selection';
import {
  DetailsBox,
  DetailsEtaText,
  FieldContainer,
  InstructionsTextarea,
  Text,
  UberBox,
} from './styled';
import { ICartDetailsProps } from './types';

const DeliveryDetailsForm: React.FC<
  React.PropsWithChildren<Omit<ICartDetailsProps, 'serviceMode'>>
> = ({
  serverOrder,
  setAreDeliveryDetailsValid,
  sectionHeading,
  phoneNumberInputRef,
  deliveryDetailsErrors,
}) => {
  const {
    deliveryAddress,
    deliveryInstructions,
    orderPhoneNumber,
    setDeliveryAddress,
    setDeliveryInstructions,
    setOrderPhoneNumber,
    updateShouldSaveDeliveryAddress,
  } = useOrderContext();
  const formatters = useIntl();
  const { formatMessage } = formatters;

  const { isOfferValidationError } = useLoyaltyContext();
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
  const selectedOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const offerFeedback = offersFeedbackMap[selectedOffer?.loyaltyEngineId || ''];

  const getBottomInlineAlertProps = (): InlineAlertProps | undefined => {
    const defaultProps = {
      borderRadius: 8,
      marginTop: '$2',
      iconCentered: true,
    };
    if (isOfferValidationError && offerFeedback && offerFeedback.length > 0) {
      return {
        ...defaultProps,
        status: 'error',
        message: DateAndTimeErrors.has(offerFeedback[0]?.code as IncentiveEvaluationErrorCodes)
          ? formatMessage({ id: 'futureOrderingOfferValidationError' })
          : getEvaluationResultMessage({
              evaluationResult: offerFeedback[0],
              formatters,
              incentiveType: PromotionType.OFFER,
            }),
      };
    }

    return undefined;
  };
  const bottomInlineAlertProps = getBottomInlineAlertProps();

  const [editingPhoneNumber, setEditingPhoneNumber] = useState(orderPhoneNumber);
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const hasPhoneNumberErrors = !!deliveryDetailsErrors.phoneNumber || !!phoneNumberError;

  const enableNumericPhoneValidation = useFlag(LaunchDarklyFlag.ENABLE_NUMERIC_PHONE_VALIDATION);

  const maxDeliveryTime = serverOrder?.delivery?.dropoff.beforeTime || '';
  const ETA = useMemo(
    () => (maxDeliveryTime ? formatDistanceToNow(new Date(maxDeliveryTime), LANGUAGES.en) : ''),
    [maxDeliveryTime]
  );

  const { region } = useLocale();
  const localizedDeliveryAddress = getLocalizedDeliveryAddress(deliveryAddress, formatMessage);

  const handleOrderPhoneNumberChange = useCallback(
    async (input: string) => {
      const newPhoneNumber = input.replace(/[^+\d]/, '');
      setEditingPhoneNumber(newPhoneNumber);

      const { valid, error } = await isPhoneNumberValid({
        phoneNumber: newPhoneNumber,
        simpleValidation: enableNumericPhoneValidation,
        country: region,
      });

      if (valid) {
        setPhoneNumberError('');
        const countryCodePhoneNumber =
          newPhoneNumber.length === PHONE_NUMBER_LENGTH
            ? `${ITU_COUNTRY_CODES[region]}${newPhoneNumber}`
            : newPhoneNumber;
        setOrderPhoneNumber(countryCodePhoneNumber);
      } else {
        setPhoneNumberError(getPhoneNumberErrorMessage(error, formatMessage));
      }

      if (setAreDeliveryDetailsValid) {
        setAreDeliveryDetailsValid(valid);
      }
    },
    [
      enableNumericPhoneValidation,
      formatMessage,
      region,
      setAreDeliveryDetailsValid,
      setOrderPhoneNumber,
    ]
  );

  const handleUnitNumberChange = (value: string) => {
    updateShouldSaveDeliveryAddress(false);
    setDeliveryAddress((previousDeliveryAddress: IPlaceAddress) => ({
      ...previousDeliveryAddress,
      addressLine2: value,
    }));
  };

  return (
    <>
      <DetailsBox accessibilityLabel={sectionHeading}>
        <UberBox>
          <Text fontSize="sm" style={{ paddingVertical: 22, paddingRight: 8 }}>
            Delivered by:
          </Text>
          <Image
            source={require('./images/uber-eats.png')}
            alt="uber eats"
            contentFit="fill"
            style={{ height: 60, width: 120 }}
          />
        </UberBox>
        {maxDeliveryTime && (
          <HStack testID="delivery-eta">
            <DetailsEtaText accessibilityLabel={formatMessage({ id: 'etaFull' })}>
              {`${formatMessage({ id: 'eta' })}: `}
            </DetailsEtaText>
            <DetailsEtaText>{ETA}</DetailsEtaText>
          </HStack>
        )}
        <Text fontSize="xs" testID="delivery-address" data-private>
          {localizedDeliveryAddress}
        </Text>
        <InlineAlert
          status="info"
          borderRadius={8}
          marginBottom="$4"
          marginTop="$3"
          message={formatMessage({ id: 'noRefundsForWrongLocaton' })}
          iconCentered
        />
        <FieldContainer>
          <TextInput
            label={formatMessage({
              id: 'unitNumberLabel',
            })}
            testID="delivery-address-line2"
            value={deliveryAddress.addressLine2}
            onChangeText={handleUnitNumberChange}
            placeholder={formatMessage({ id: 'enterUnitPlaceholder' })}
            placeholderTextColor={'__legacyToken.icon-form-input-default'}
            InputRightElement={
              deliveryAddress.addressLine2?.length > 0 ? (
                <Box paddingX="$2.5">
                  <Icon variant="check" color={Styles.color.validated} />
                </Box>
              ) : undefined
            }
          />
        </FieldContainer>
        <FieldContainer>
          <FormControl.Label>
            {formatMessage({ id: 'deliveryDetailsInstructions' })}
          </FormControl.Label>
          <InstructionsTextarea
            onChangeText={text => setDeliveryInstructions(text)}
            placeholder={formatMessage({ id: 'deliveryInstructionsPlaceholder' })}
            placeholderTextColor={'__legacyToken.icon-form-input-default'}
            value={deliveryInstructions}
            returnKeyType="done"
            testID="deliveryInstructions"
            maxLength={512}
            date-private
            numberOfLines={1}
          />
        </FieldContainer>
        <FieldContainer>
          <TextInput
            onChangeText={handleOrderPhoneNumberChange}
            testID="delivery-phone-number"
            label={formatMessage({ id: 'phoneNumberRequired' })}
            labelProps={{
              _text: {
                color: hasPhoneNumberErrors ? 'error.600' : undefined,
              },
            }}
            value={editingPhoneNumber}
            keyboardType="phone-pad"
            errorMessage={deliveryDetailsErrors.phoneNumber || phoneNumberError}
            _focus={{
              borderColor: hasPhoneNumberErrors ? 'token.border-color-error' : undefined,
            }}
            required
            placeholder={formatMessage({ id: 'phoneNumberPlaceholder' })}
            placeholderTextColor={'__legacyToken.icon-form-input-default'}
            ref={phoneNumberInputRef}
            InputRightElement={
              !phoneNumberError && orderPhoneNumber ? (
                <Box paddingX="$2.5">
                  <Icon variant="check" color={Styles.color.validated} />
                </Box>
              ) : (
                <Box /> // We need this empty <Box /> to prevent a bigger rerender, avoiding the keyboard for being dismissed during the process
              )
            }
          />
        </FieldContainer>
        {/* TODO: RN - Revisit and see how/if we should still handle country select (not being used currently) */}
        {/* {enableNumericPhoneValidation ? (
        <TextInput
          onChange={(e: NativeSyntheticEvent<TextInputChangeEventData>) => {
            handleOrderPhoneNumberChange(e.nativeEvent.text.replace(/[^+0-9]/, ''));
          }}
          testID="delivery-phone-number"
          label={formatMessage({ id: 'phoneNumberRequired' })}
          value={orderPhoneNumber}
          // validation={
          //   phoneNumberError || !orderPhoneNumber
          //     ? FormValidationState.INVALID
          //     : FormValidationState.VALID
          // }
          errorMessage={phoneNumberError}
        />
      ) : (
        <FloatingLabelPhoneInput
          hideCountrySelector
          testID="delivery-phone-number"
          label={formatMessage({ id: 'phoneNumberRequired' })}
          onChange={handleOrderPhoneNumberChange}
          value={orderPhoneNumber}
          placeholder={formatMessage({ id: 'phoneNumberPlaceholder' })}
          // validation={
          //   phoneNumberError || !orderPhoneNumber
          //     ? FormValidationState.INVALID
          //     : FormValidationState.VALID
          // }
          message={phoneNumberError}
        />
      )} */}

        <PhoneAddressInformationToggle />
        <SaveAddressSelection />
      </DetailsBox>
      {bottomInlineAlertProps && <InlineAlert {...bottomInlineAlertProps} />}
    </>
  );
};

export default DeliveryDetailsForm;
